/**
 * Subscription Plans Configuration
 * Single source of truth for all plan definitions
 *
 * IMPORTANT: Keep usage limits and videoProcessingLimit in sync with:
 * - api/paddle-webhook/index.js
 * - api/update-subscription/index.js
 * - api/verify-subscription/index.js
 */

export const PLANS = {
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'For individuals and small teams',
    price: 79,
    currency: 'USD',
    period: 'month',
    usageLimit: 2000,
    videoProcessingLimit: 2,
    features: [
      '2,000 object annotations/month',
      '2 fully automated video processings/month (4 hour limit)',
      'Automatic court calibration for pixel → real-world coordinates',
      'Calibration fine tuning for high accuracy',
      'All AI annotation models',
      'Local + Azure storage support',
      'Export to JSON',
      'Limited Email support',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Label without limits',
    price: 599,
    currency: 'USD',
    period: 'month',
    usageLimit: null, // Unlimited
    videoProcessingLimit: 15,
    features: [
      'Everything in Starter',
      '15 fully automated video processings/month (30 hour limit)',
      'Unlimited object annotations',
      'Priority access for new models and AI features',
      'Priority support with faster response times',
    ],
  },
};

/**
 * Enterprise plan - display only, not linked to billing
 */
export const ENTERPRISE_PLAN = {
  id: 'enterprise',
  name: 'Enterprise',
  description: 'For larger organizations',
  price: null, // Custom pricing
  currency: 'USD',
  period: 'month',
  usageLimit: null,
  isDisplayOnly: true,
  features: [
    'Everything in Pro',
    'Custom fully automated video processings/month',
    'Custom integrations & API access',
    'Custom SLA & support agreements',
    'Volume discounts',
  ],
};

export const TRIAL_DURATION_DAYS = 7;

/** Video processing limit for trial (no paid plan yet). Keep in sync with API when setting trial user in Firestore. */
export const TRIAL_VIDEO_PROCESSING_LIMIT = 1;

/**
 * Subscription statuses
 */
export const SUBSCRIPTION_STATUS = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

/**
 * Get plan by ID
 */
export const getPlan = (planId) => PLANS[planId] || null;

/**
 * Get all paid plans (excludes trial)
 */
export const getPaidPlans = () => Object.values(PLANS);

/**
 * Get usage limit for a plan
 */
export const getUsageLimit = (planId) => {
  const plan = PLANS[planId];
  return plan ? plan.usageLimit : null;
};

/**
 * Get video processing limit for a plan (videos allowed to send to pipeline).
 * Returns null for unlimited. Trial uses TRIAL_VIDEO_PROCESSING_LIMIT (set in AuthContext / API).
 */
export const getVideoProcessingLimit = (planId) => {
  const plan = PLANS[planId];
  return plan && 'videoProcessingLimit' in plan ? plan.videoProcessingLimit : null;
};

/**
 * Format price for display (e.g., "$149")
 */
export const formatPrice = (planId) => {
  const plan = PLANS[planId];
  return plan ? `$${plan.price}` : null;
};

/**
 * Format usage limit for display (e.g., "2,000")
 */
export const formatUsageLimit = (planId) => {
  const plan = PLANS[planId];
  if (!plan) return null;
  return plan.usageLimit ? plan.usageLimit.toLocaleString() : 'Unlimited';
};

export default PLANS;
